import React  from "react";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
} from "react-router-dom";
import { OplaesProvider } from './providers/OplaesProvider';
import { StrategyProvider } from './providers/StrategyProvider';
import Community from './components/Oplaes';
import Oplaes from './components/oplaes/index';

import "./global.css"
import "./components/Creator.css";

import InstagramConnect from "./components/InstagramConnect";
import Navigation from "./components/Navigation";
import { UserProvider } from './providers/UserProvider';
import VideoLibrary from './components/VideoLibrary';
import Login from './components/TiktokAuth';
import { VideosProvider } from './providers/VideosProvider';
import AutomationPage from './components/AutomationPage';
import CrossPostPage from './components/CrossPostPage';
import DashboardPage from './components/DashboardPage';
import EmailCapture from './components/EmailCapture';
import ConnectedAccountsPage from './components/ConnectedAccountsPage';
import CalendarPage from './components/CalendarPage';
import Upgrade from './components/Upgrade';
import DownloadWebsocketProvider from './providers/DownloadWebsocketProvider';
import PlatformWebsocketProvider from './providers/PlatformWebsocketProvider';

export const REFERRAL_PREFIX = "T-";

// Custom Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  // componentDidCatch(error, errorInfo) {
  //   // Log the error to Bugsnag
  // }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 text-center space-y-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Something went wrong</h2>
            <p className="text-gray-600">We've been notified and are working to fix the issue.</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Initialize GA
window.dataLayer = window.dataLayer || [];
function gtag() {
  window.dataLayer.push(arguments);
}
gtag('js', new Date());
gtag('config', 'AW-16749159264', {
  send_page_view: false
});

// Layout component that includes Navigation
const MainLayout = () => (
  <Navigation>
    <Outlet />
  </Navigation>
);

// Wrap AutomationPage with OplaesProvider
const AutomationPageWithProvider = () => (
  <OplaesProvider>
    <AutomationPage />
  </OplaesProvider>
);

// Wrap Community with StrategyProvider only
const CommunityWithProviders = () => (
  <StrategyProvider>
    <Community />
  </StrategyProvider>
);

const router = createBrowserRouter([
  {
    path: '/onboarding',
    element: <EmailCapture onComplete={() => window.location.href = '/'} />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: '/connect/instagram',
    element: <InstagramConnect />,
  },
  {
    element: <MainLayout />,
    children: [
      {
        path: '/library',
        element: (
          <VideosProvider>
            <VideoLibrary />
          </VideosProvider>
        ),
      },
      {
        path: '/filter',
        element: (
          <VideosProvider>
            <VideoLibrary />
          </VideosProvider>
        ),
      },
      {
        path: '/oplaes',
        element: <Oplaes />,
      },
      {
        path: '/community',
        element: <CommunityWithProviders />,
      },
      {
        path: '/automation',
        element: <AutomationPageWithProvider />,
      },
      {
        path: '/stats',
        element: <DashboardPage />,
      },
      {
        path: '/calendar',
        element: <CalendarPage />,
      },
      {
        path: '/settings',
        element: <ConnectedAccountsPage />,
      },
      {
        path: '/cross-post',
        element: <CrossPostPage />,
      },
      {
        path: '/',
        element: (
          <VideosProvider>
            <VideoLibrary />
          </VideosProvider>
        ),
      },
      {
        path: '/:referralCode',
        element: (
          <VideosProvider>
            <VideoLibrary />
          </VideosProvider>
        ),
      },
      {
        path: '/upgrade',
        element: <Upgrade />,
      },
      {
        path: '/upgrade-success',
        element: <Upgrade />,
      }
    ]
  },
]);

const App = () => {
  return (
    <ErrorBoundary>
      <UserProvider>
        <OplaesProvider>
          <PlatformWebsocketProvider>
            <DownloadWebsocketProvider>
              <RouterProvider router={router} />
            </DownloadWebsocketProvider>
          </PlatformWebsocketProvider>
        </OplaesProvider>
      </UserProvider>
    </ErrorBoundary>
  );
};

createRoot(document.getElementById('root')).render(<App />);

export default App;
